import React, { useRef, useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import CanvasDraw from 'react-canvas-draw';
import { Undo2, Redo2, Eraser, Palette } from 'lucide-react';

const COLORS = [
  { name: 'Negro', value: '#1a1a2e' },
  { name: 'Rojo', value: '#e74c3c' },
  { name: 'Azul', value: '#3498db' },
  { name: 'Blanco', value: '#ffffff' },
];

const DrawingCanvas = forwardRef(({ onDrawingChange }, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [currentColor, setCurrentColor] = useState(COLORS[1].value); // Rojo por defecto para que se vea
  const [brushRadius, setBrushRadius] = useState(4);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [canvasWidth, setCanvasWidth] = useState(400);

  // Ajustar el ancho del canvas al contenedor
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth - 4; // -4 por el borde
        setCanvasWidth(Math.max(300, width));
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getDrawingData: () => {
      if (canvasRef.current) {
        return canvasRef.current.getDataURL('png', false, '#0a0a1a');
      }
      return null;
    },
    getSaveData: () => {
      if (canvasRef.current) {
        return canvasRef.current.getSaveData();
      }
      return null;
    },
    clear: () => {
      if (canvasRef.current) {
        canvasRef.current.clear();
        setUndoStack([]);
        setRedoStack([]);
      }
    },
    isEmpty: () => {
      if (canvasRef.current) {
        const data = canvasRef.current.getSaveData();
        const parsed = JSON.parse(data);
        return parsed.lines.length === 0;
      }
      return true;
    }
  }));

  const handleUndo = () => {
    if (canvasRef.current) {
      const currentData = canvasRef.current.getSaveData();
      const parsed = JSON.parse(currentData);
      
      if (parsed.lines.length > 0) {
        // Save current state to redo stack
        const lastLine = parsed.lines.pop();
        setRedoStack(prev => [...prev, lastLine]);
        
        // Clear and redraw
        canvasRef.current.clear();
        if (parsed.lines.length > 0) {
          canvasRef.current.loadSaveData(JSON.stringify(parsed), true);
        }
      }
    }
  };

  const handleRedo = () => {
    if (canvasRef.current && redoStack.length > 0) {
      const currentData = canvasRef.current.getSaveData();
      const parsed = JSON.parse(currentData);
      
      // Get last undone line
      const lineToRedo = redoStack[redoStack.length - 1];
      setRedoStack(prev => prev.slice(0, -1));
      
      // Add it back
      parsed.lines.push(lineToRedo);
      canvasRef.current.loadSaveData(JSON.stringify(parsed), true);
    }
  };

  const handleClear = () => {
    if (canvasRef.current) {
      // Save current state before clearing
      const currentData = canvasRef.current.getSaveData();
      const parsed = JSON.parse(currentData);
      if (parsed.lines.length > 0) {
        setUndoStack(prev => [...prev, currentData]);
      }
      canvasRef.current.clear();
      setRedoStack([]);
    }
  };

  const handleChange = () => {
    // Clear redo stack when new drawing is made
    setRedoStack([]);
    if (onDrawingChange) {
      onDrawingChange();
    }
  };

  return (
    <div className="drawing-canvas-container">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 mb-3 p-2 bg-[hsl(232,20%,14%)] rounded-lg">
        {/* Colors */}
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-white/50" />
          <div className="flex gap-1">
            {COLORS.map((color) => (
              <button
                key={color.value}
                onClick={() => setCurrentColor(color.value)}
                data-testid={`color-${color.name.toLowerCase()}-button`}
                className={`w-7 h-7 rounded-full border-2 transition-all ${
                  currentColor === color.value 
                    ? 'border-[hsl(198,62%,54%)] scale-110' 
                    : 'border-transparent hover:border-white/30'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              />
            ))}
          </div>
        </div>

        {/* Brush size */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/50">Grosor:</span>
          <input
            type="range"
            min="2"
            max="12"
            value={brushRadius}
            onChange={(e) => setBrushRadius(parseInt(e.target.value))}
            data-testid="brush-size-slider"
            className="w-16 h-1.5 bg-[hsl(232,18%,18%)] rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleUndo}
            data-testid="undo-button"
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            title="Deshacer"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            data-testid="redo-button"
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Rehacer"
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleClear}
            data-testid="clear-canvas-button"
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            title="Limpiar todo"
          >
            <Eraser className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div 
        ref={containerRef}
        className="border-2 border-dashed border-[hsl(232,18%,25%)] rounded-lg overflow-hidden bg-[#0a0a1a]"
      >
        <CanvasDraw
          ref={canvasRef}
          brushColor={currentColor}
          brushRadius={brushRadius}
          lazyRadius={0}
          canvasWidth={canvasWidth}
          canvasHeight={280}
          backgroundColor="#0a0a1a"
          hideGrid={true}
          onChange={handleChange}
        />
      </div>

      <p className="text-xs text-white/40 mt-2 text-center">
        Dibuja lo que percibiste. Usa los colores y el borrador para ajustar.
      </p>
    </div>
  );
});

DrawingCanvas.displayName = 'DrawingCanvas';

export default DrawingCanvas;
